/**
 * ItemTypeChangeDialog - Confirmation dialog for item type changes
 *
 * Shows warning when changing item type will cause data loss.
 * Supports multiple field types: subtitle, image, checkbox, description, title.
 */

import React from 'react';
import { Typography, Box } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { SDialog } from '@modules/ui-components';

/**
 * Fields that will be cleared on type change
 */
export interface FieldsToClear {
  clearSubtitle?: boolean;
  clearImage?: boolean;
  clearCheckbox?: boolean;
  clearDescription?: boolean;
  clearTitle?: boolean;
  clearSubtitle1?: boolean;
  clearSubtitle2?: boolean;
}

export interface ItemTypeChangeDialogProps {
  /** Whether dialog is open */
  open: boolean;

  /** Callback when dialog is closed (cancel) */
  onClose: () => void;

  /** Callback when change is confirmed */
  onConfirm: () => void;

  /** Fields that will be cleared */
  fieldsToClear: FieldsToClear;

  /** Current item type label */
  fromType: string;

  /** New item type label */
  toType: string;
}

/**
 * Build warning message based on fields to clear
 */
function getWarningMessage(fieldsToClear: FieldsToClear): string {
  const fieldsToRemove: string[] = [];

  if (fieldsToClear.clearSubtitle || fieldsToClear.clearSubtitle1 || fieldsToClear.clearSubtitle2) {
    fieldsToRemove.push('subtitle');
  }
  if (fieldsToClear.clearImage) {
    fieldsToRemove.push('image');
  }
  if (fieldsToClear.clearCheckbox) {
    fieldsToRemove.push('checkbox');
  }
  if (fieldsToClear.clearDescription) {
    fieldsToRemove.push('description');
  }
  if (fieldsToClear.clearTitle) {
    fieldsToRemove.push('title');
  }

  if (fieldsToRemove.length === 0) {
    return 'This change is safe and will not remove any data.';
  }

  const fieldList = fieldsToRemove.join(', ');
  return `Changing item type will remove ${fieldList} data from all items. This action cannot be undone.`;
}

/**
 * ItemTypeChangeDialog - Confirmation dialog for data-loss changes
 */
export const ItemTypeChangeDialog: React.FC<ItemTypeChangeDialogProps> = ({
  open,
  onClose,
  onConfirm,
  fieldsToClear,
  fromType,
  toType,
}) => {
  const warningMessage = getWarningMessage(fieldsToClear);

  return (
    <SDialog
      open={open}
      onClose={onClose}
      title="Change item type?"
      maxWidth="xs"
      fullWidth
      data-testid="item-type-change-dialog"
      // Destructive confirmation (type change loses existing data).
      showCloseButton={false}
      primaryAction={{
        label: 'Confirm',
        onClick: onConfirm,
        color: 'error',
        'data-testid': 'confirm-type-change-button',
      }}
      secondaryAction={{
        label: 'Cancel',
        onClick: onClose,
        'data-testid': 'cancel-type-change-button',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <WarningAmberIcon color="warning" sx={{ mt: 0.5 }} />
          <Box>
            <Typography variant="body1" color="text.primary" gutterBottom>
              {fromType} → {toType}
            </Typography>
            <Typography variant="body2" color="text.secondary" data-testid="data-loss-warning">
              {warningMessage}
            </Typography>
          </Box>
        </Box>
      </Box>
    </SDialog>
  );
};

export default ItemTypeChangeDialog;
